// Filename: models/questionnaire/questionnaire.js
define('models/goal/goal',[
    'underscore', 'backbone', 'mustache', 'utils', 'moment',
    // services
    'services/api',
    'services/user',
    'services/unit-converter',
    'services/i18n',
    // models
    'models/calendar/personal-data/personal-data-type'
], function(_, Backbone, Mustache, U, moment, API, User, UnitConverter, i18n, MPersonalDataType) {

    var round = Math.round,
        CALENDAR_ITEM_TYPES = {
            minutes: 'activity',
            distance: 'activity',
            custom: 'custom_data'
        },
        CALENDAR_ITEM_ATTR = {
            minutes: 'duration',
            distance: 'distanceValue',
            custom: 'value'
        };

    var MGoal = Backbone.Epoxy.Model.extend({
        idAttribute: 'id',
        endValueComputeTypeAttribute: 'sumGoal',

        urlRoot: function() {
          var url = "goals/";

          if (this.user) {
             url = Mustache.render("/users/{{{user}}}/goals/", { user: this.user.attributes._id });
          }

          return API.url(url);
        },

        blacklist: ['currentValue', 'customData_unit', 'customData_name', 'convertible'],

        defaults: function() {
            return {
                endValue: "",
                startValue: "",
                currentValue: "",
                type: "custom",
                customData_id: "",
                sumGoal: true,
                convertible: true,
                startDate: API.formatDate(moment()),
                endDate: API.formatDate(moment().add(1, 'months'))
            };
        },

        save: function(attrs, options) {
            if (this.getType() !== 'custom') {
                this.attributes = _.omit(this.attributes, ['customData_id', 'sumGoal']);
            }

            return MGoal.__super__.save.apply(this, [attrs, options]);
        },

        computeds: {
            goalTarget: {
                deps: ["endValue"],
                get: function(endValue) {
                    if (_.isObject(endValue)) endValue = endValue.value;
                    if (this.isDuration()) endValue = this.convertSecToMin(endValue);

                    return endValue;
                },
                set: function(endValue) {
                    return {
                        endValue: { value: endValue, unit: this.get('goalUnit') }
                    };
                }
            },

            goalUnit: {
                deps: ["endValue"],
                get: function(endValue) {
                    if (this.isDuration()) return 'min';
                    if (this.isConvertibleCustom()) return this.getUserUnit(this.get('customData_unit'));
                    if (this.isCustom()) return this.get('customData_unit');
                    return i18n.t(endValue.unit || this.get('customData_unit'));
                },
                set: function(goalUnit) {
                    return {
                        endValue: { value: this.get('goalTarget'), unit: goalUnit }
                    };
                }
            },

            startValueUnit: {
                deps: ["startValue"],
                get: function(startValue) {
                    return startValue ? startValue.unit : this.get('customData_unit');
                },
                set: function(unit) {
                    return {
                        startValue: { value: this.get('start'), unit: unit }
                    };
                }
            },

            daysRemaining: {
                deps: ["endDate"],
                get: function(endDate) {
                    endDate = endDate ? moment(endDate).diff(moment(), 'days') : 0;
                    return endDate > 0 ? endDate : 0;
                }
            },

            timeleftPercentage: {
                deps: ["startDate", "endDate"],
                get: function(start, end) {
                    var today = moment().toDate().setHours(0, 0, 0, 0);
                    start = new Date(start).setHours(0, 0, 0, 0);
                    end = new Date(end).setHours(0, 0, 0, 0);

                    if (today <= start) {
                        return 0;
                    }

                    return 100 - round((end - today) * 100 / (end - start));
                }
            },

            goalPercentage: {
                deps: ["goalTarget", "convertedCurrentValue", "start"],
                get: function(goalTarget, current, start) {
                    var percentage;
                    if (goalTarget >= start) {
                        percentage = (current - start) / (goalTarget - start);
                    } else {
                        percentage = (start - current) / (start - goalTarget);
                    }
                    return percentage > 0 ? round(percentage * 100) : 0;
                }
            },

            start: {
                deps: ["startValue"],
                get: function(startValue) {
                    if (_.isObject(startValue)) {
                        startValue = startValue.value;
                    }

                    return startValue;
                },
                set: function(startValue) {
                    var value = { value: startValue, unit: this.get('goalUnit') };
                    if (this.isCustom()) value = startValue;
                    return {
                        startValue: value
                    };
                }
            },

            goalDisplayUnit: {
              deps: ["currentValue", "endValue"],
               get: function() {
                  return this.getGoalDisplayUnit();
               }
           },

            convertedCurrentValue: {
                deps: ["currentValue"],
                get: function(currentValue) {
                    var value = _.isObject(currentValue) ? currentValue.value : currentValue;
                    var displayUnit = this.getGoalDisplayUnit();
                    // If we ca find a way to determine what is currentValue's unit, we're alright
                    return round(this.getChartConvertedValue(value, currentValue, displayUnit));
                }
            },

            convertedTargetValue: {
                deps: ["endValue"],
                get: function(endValue) {
                    var value = _.isObject(endValue) ? endValue.value : endValue;
                    var displayUnit = this.getGoalDisplayUnit();

                    return round(this.getChartConvertedValue(value, endValue, displayUnit));
                }
            },

            chartLabel: {
                deps: ["type"],
                get: function(type) {
                    return i18n.t(this.getChartLabel());
                }
            },

            timeHours: {
                deps: ["endValue"],
                get: function(duration) {
                    return duration ? Math.floor(duration / 3600) || "" : "";
                },
                set: function(hours) {
                    hours = parseInt(hours) || 0;
                    var endValue = parseInt(this.get('endValue')) || 0,
                        oldHours = parseInt(this.get('timeHours')) || 0;

                    return {
                        endValue: endValue + (hours - oldHours) * 3600
                    };
                },
            },

            timeMinutes: {
                deps: ["endValue"],
                get: function(duration) {
                    return duration ? Math.floor(duration / 60 % 60) || "" : "";
                },
                set: function(minutes) {
                    minutes = parseInt(minutes) || 0;
                    var endValue = parseInt(this.get('endValue')) || 0,
                        oldMinutes = parseInt(this.get('timeMinutes')) || 0;

                    if (minutes > 59) minutes = 59;
                    if (oldMinutes === minutes) this.trigger('change:timeMinutes', this);

                    return {
                        endValue: endValue + (minutes - oldMinutes) * 60
                    };
                },

            }
        },

        getGoalDisplayUnit: function() {
            if (this.isDuration()) {
                return 'min';
            } else if (this.isDistance()) {
                return User.get("distanceUnit");
            } else if (this.isConvertibleCustom()) {
                return this.getUserUnit(this.get('customData_unit'));
            } else if (this.isCustom()) {
                return this.get('customData_unit');
            }

            return '';
        },

        getChartConvertedValue: function(value, endValue, displayUnit) {
            if (! this.isNew()) {
                if (this.isDuration()) {
                    value = this.convertSecToMin(value);
                } else if (this.isConvertibleCustom()) {
                    value = endValue ? UnitConverter.convert(value, endValue.unit, displayUnit) : 0;
                } else {
                    value = endValue ? value : 0;
                }
            }

            return value;
        },

        isCustom: function() {
            return this.getType() === 'custom';
        },

        isDuration: function() {
            return this.getType() === 'minutes';
        },

        isDistance: function() {
            return this.getType() === 'distance';
        },

        setRelatedTypeUnit: function(units) {
            var type = this.getType(),
                unit = units[type];

            if (!unit) return;
            this.set({ goalUnit: unit, startValueUnit: unit });
        },

        setUser: function(usr) {
            this.user = usr;
        },

        convertSecToMin: function(seconds) {
            return seconds / 60;
        },

        isConvertible: function() {
            return this.get('convertible');
        },

        isConvertibleCustom: function() {
            return this.isConvertible() && this.isCustom();
        },

        getChartLabel: function() {
            var type = this.getType(),
                        label;
            var isGain = false;
            if (!_.isEmpty(this.get('endValue')) && !_.isEmpty(this.get('endValue'))) {
                if (this.get('startValue').value !== undefined && this.get('endValue').value !== undefined)
                    isGain = this.get('endValue').value - this.get('startValue').value > 0;
              }
            switch (type) {
                case 'minutes':
                    label = 'Training Duration';
                    break;
                case 'distance':
                    label = 'Distance';
                    break;
                case 'custom':
                    label = this.get('customData_name');
                    break;
            }

            return label;
        },

        getProgressColor: function() {
            var progress = this.get('goalPercentage');

            if (progress < 40) {
                return 'goal-alert';
            } else if (progress < 80) {
                return 'goal-warning';
            } else {
                return 'goal-success';
            }
        },

        getProfileUnitAttr: function() {
            if (this.isDistance()) {
                return 'distanceUnit';
            }
        },

        getCalendarItemType: function() {
            return CALENDAR_ITEM_TYPES[this.getType()];
        },

        getCalendarItemAttr: function() {
            return CALENDAR_ITEM_ATTR[this.getType()];
        },

        getType: function() {
            return this.get('type');
        },

        getTypes: function() {
            return new Backbone.Collection([
                { label: 'Distance travelled', value: 'distance' },
                { label: 'Training time', value: 'minutes' },
                { label: 'Custom goal', value: 'custom' }
            ]);
        },

        getUserUnit: function(unit) {
            switch (unit) {
                case MPersonalDataType.prototype.lengthUnits: return User.get('lengthUnit');
                case MPersonalDataType.prototype.weightUnits: return User.get('weightUnit');
                case MPersonalDataType.prototype.distanceUnits: return User.get('distanceUnit');
                default: return unit;
            }
        },

        validation: {
            type: {
                presence: true,
            },
            startValue: {
                presence: true,
            },
            endValue: {
                presence: true,
            },
            startDate: {
                presence: true,
            },
            endDate: {
                presence: true,
            },

        },


    });

    // Return the model for the module
    return MGoal;
});

